import { useMemo, useState } from "react";
import { Dialog, Drawer } from "@mui/material";
import { Icon } from "@iconify/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { toast } from "react-toastify";
import { Box, HStack, VStack } from "../../components/utils";
import { CheckBoxWithLabel, Input } from "../../components/shared/InputField";
import Tooltip from "../../components/shared/Tooltip";
import {
  PrimaryCTAButton,
  SecondaryCTAButton,
} from "../quickRound/CTAButtonComponents";
import { useCompanyRoles } from "../../queries/newCapTable/userRoles";
import {
  useBulkAssignUsers,
  useDeleteCompanyUser,
} from "../../queries/companyUsers";

export type BulkCompany = { uuid: string; nameOfTheCompany: string };
export type PickableUser = { fullName: string; emailId: string };
export type AccessibleUser = {
  userId: number;
  emailId: string;
  fullName?: string;
};

type Props = {
  /** companies pre-selected from the grid (initial value; editable in-drawer) */
  companies: BulkCompany[];
  /** full company list, so companies can be added inside the drawer */
  allCompanies: BulkCompany[];
  /** de-duplicated pool of existing users to pick from */
  existingUsers: PickableUser[];
  onClose: () => void;
  /** called after a successful (full or partial) grant so the caller can clear selection */
  onDone: () => void;
  /** when true, the company list is fixed to the initial `companies` — no add/remove UI */
  lockCompanies?: boolean;
  /** users who already have access to the (locked) company — shown below the
   * pickable list, excluded from it, and removable */
  accessibleUsers?: AccessibleUser[];
};

// Bulk-assign always grants this role/designation combination.
const ROLE_NAME = "Admin";
const DESIGNATION = "Others";

function BulkAssignDrawerContent({
  companies: initialCompanies,
  allCompanies,
  existingUsers,
  onClose,
  onDone,
  lockCompanies = false,
  accessibleUsers = [],
}: Props) {
  const { data: roles } = useCompanyRoles();
  const { mutate: bulkAssign, isLoading } = useBulkAssignUsers();
  const { mutate: deleteCompanyUser } = useDeleteCompanyUser();

  const [companies, setCompanies] = useState<BulkCompany[]>(initialCompanies);
  const [showCompanyPicker, setShowCompanyPicker] = useState(false);
  const [companySearch, setCompanySearch] = useState("");

  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [userSearch, setUserSearch] = useState("");
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);
  const [removeTarget, setRemoveTarget] = useState<AccessibleUser | null>(
    null
  );

  const [silentAccess, setSilentAccess] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  // Grants for the fixed role, matched by name against the API roles.
  const selectedRole = roles?.find((r) => r.name === ROLE_NAME);

  const selectedCompanyIds = useMemo(
    () => new Set(companies.map((c) => c.uuid)),
    [companies]
  );

  const addableCompanies = useMemo(() => {
    const q = companySearch.trim().toLowerCase();
    return allCompanies
      .filter((c) => !selectedCompanyIds.has(c.uuid))
      .filter((c) => !q || c.nameOfTheCompany.toLowerCase().includes(q));
  }, [allCompanies, selectedCompanyIds, companySearch]);

  const removeCompany = (uuid: string) =>
    setCompanies((prev) => prev.filter((c) => c.uuid !== uuid));

  const addCompany = (company: BulkCompany) =>
    setCompanies((prev) =>
      prev.some((c) => c.uuid === company.uuid) ? prev : [...prev, company]
    );

  const addUserEmail = (email: string) => {
    if (!email) return;
    setSelectedEmails((prev) => {
      if (prev.has(email)) return prev;
      const next = new Set(prev);
      next.add(email);
      return next;
    });
  };

  const removeUserEmail = (email: string) =>
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      next.delete(email);
      return next;
    });

  const accessibleEmailSet = useMemo(
    () => new Set(accessibleUsers.map((u) => u.emailId.toLowerCase())),
    [accessibleUsers]
  );

  // Only account users can have their access revoked here.
  const accountUserEmailSet = useMemo(
    () => new Set(existingUsers.map((u) => u.emailId.toLowerCase())),
    [existingUsers]
  );

  // Users left to grant access to — excludes anyone who already has access.
  const pickableUsers = useMemo(
    () =>
      existingUsers.filter(
        (u) => !accessibleEmailSet.has(u.emailId.toLowerCase())
      ),
    [existingUsers, accessibleEmailSet]
  );

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return pickableUsers;
    return pickableUsers.filter(
      (u) =>
        u.fullName?.toLowerCase().includes(q) ||
        u.emailId.toLowerCase().includes(q)
    );
  }, [pickableUsers, userSearch]);

  // Final set of users to grant = the checked users.
  const selectedUsers: PickableUser[] = useMemo(
    () => pickableUsers.filter((u) => selectedEmails.has(u.emailId)),
    [pickableUsers, selectedEmails]
  );

  const handleRemoveAccess = (u: AccessibleUser) => {
    const companyId = companies[0]?.uuid;
    if (!companyId) return;
    setRemoveTarget(null);
    setDeletingUserId(u.userId);
    deleteCompanyUser(
      { companyId, userId: u.userId },
      {
        onSuccess: () => {
          toast(`Removed access for ${u.fullName || u.emailId}`, {
            type: "success",
            autoClose: 2000,
          });
          setDeletingUserId(null);
        },
        onError: (err: any) => {
          toast(
            err?.response?.data?.errorMessage || "Failed to remove access",
            { type: "error", autoClose: 3000 }
          );
          setDeletingUserId(null);
        },
      }
    );
  };

  const totalGrants = companies.length * selectedUsers.length;

  const companiesError = submitted && companies.length === 0;
  const usersError = submitted && selectedUsers.length === 0;

  const handleSubmit = () => {
    setSubmitted(true);
    if (companies.length === 0 || selectedUsers.length === 0) return;

    const fullNameByEmail: Record<string, string> = {};
    selectedUsers.forEach((u) => {
      if (u.fullName && u.fullName !== u.emailId)
        fullNameByEmail[u.emailId] = u.fullName;
    });

    bulkAssign(
      {
        companyIds: companies.map((c) => c.uuid),
        emails: selectedUsers.map((u) => u.emailId),
        roleName: ROLE_NAME,
        designation: DESIGNATION,
        roleGrants: selectedRole?.acl || [],
        silentAccess,
        fullNameByEmail,
      },
      {
        onSuccess: () => {
          toast(
            `Granted access to ${companies.length} ${
              companies.length === 1 ? "company" : "companies"
            } for ${selectedUsers.length} ${
              selectedUsers.length === 1 ? "user" : "users"
            }`,
            { type: "success", autoClose: 2500 }
          );
          onDone();
          onClose();
        },
        onError: (err: any) => {
          toast(
            err?.response?.data?.errorMessage || "Bulk assignment failed",
            { type: "error", autoClose: 3500 }
          );
        },
      }
    );
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <HStack className="shrink-0 items-center justify-between border-b border-stone-200 px-5 py-4">
        <VStack className="min-w-0">
          <p className="text-base font-semibold text-stone-800">
            {lockCompanies ? "Manage Users" : "Bulk assign users"}
          </p>
          <p className="truncate text-xs text-stone-500" title={companies[0]?.nameOfTheCompany}>
            {lockCompanies
              ? companies[0]?.nameOfTheCompany || ""
              : `${companies.length} ${
                  companies.length === 1 ? "company" : "companies"
                } selected`}
          </p>
        </VStack>
        <XMarkIcon
          className="h-6 w-6 shrink-0 cursor-pointer text-stone-500 hover:text-stone-700"
          onClick={onClose}
        />
      </HStack>

      <div className="flex-1 overflow-y-auto p-5">
        {/* Selected companies */}
        <HStack className="mb-2 items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">
            Companies{companies.length > 0 ? ` (${companies.length})` : ""}
          </p>
          {!lockCompanies && (addableCompanies.length > 0 || showCompanyPicker) ? (
            <button
              type="button"
              onClick={() => setShowCompanyPicker((s) => !s)}
              className="flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/70"
            >
              <Icon
                icon={showCompanyPicker ? "mdi:minus" : "mdi:plus"}
                height={16}
              />
              {showCompanyPicker ? "Done" : "Add companies"}
            </button>
          ) : null}
        </HStack>
        <Box
          className={`mb-3 max-h-28 overflow-y-auto rounded-md border bg-stone-50 p-2 ${
            companiesError ? "border-red-400" : "border-stone-200"
          }`}
        >
          {companies.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {companies.map((c) => (
                <span
                  key={c.uuid}
                  className="flex max-w-full items-center gap-1 truncate rounded-full bg-white px-2.5 py-1 text-xs font-medium text-stone-700 ring-1 ring-stone-200"
                  title={c.nameOfTheCompany}
                >
                  <span className="truncate">{c.nameOfTheCompany}</span>
                  {!lockCompanies && (
                    <Icon
                      icon="mdi:close"
                      height={14}
                      className="shrink-0 cursor-pointer text-stone-400 hover:text-red-500"
                      onClick={() => removeCompany(c.uuid)}
                    />
                  )}
                </span>
              ))}
            </div>
          ) : (
            <p className="px-1 py-2 text-xs text-stone-400">
              No companies selected — add at least one below.
            </p>
          )}
        </Box>
        {companiesError && (
          <span className="text-xxs text-red-500">
            Add at least one company
          </span>
        )}

        {/* Add companies picker */}
        {!lockCompanies && showCompanyPicker && (
          <VStack className="mb-5 gap-2 rounded-md border border-dashed border-stone-300 p-3">
            <Input
              type="text"
              placeholder="Search companies to add"
              value={companySearch}
              onChange={(e: any) => setCompanySearch(e.target.value)}
            />
            <VStack className="max-h-44 divide-y divide-stone-100 overflow-y-auto rounded-md border border-stone-200 bg-white">
              {addableCompanies.length > 0 ? (
                addableCompanies.map((c) => (
                  <HStack
                    key={c.uuid}
                    className="items-center justify-between px-3 py-2 hover:bg-stone-50"
                  >
                    <span
                      className="truncate text-sm text-stone-700"
                      title={c.nameOfTheCompany}
                    >
                      {c.nameOfTheCompany}
                    </span>
                    <button
                      type="button"
                      onClick={() => addCompany(c)}
                      className="flex shrink-0 items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary hover:bg-primary/20"
                    >
                      <Icon icon="mdi:plus" height={14} /> Add
                    </button>
                  </HStack>
                ))
              ) : (
                <Box className="py-4">
                  <p className="text-center text-xs text-stone-400">
                    All companies added
                  </p>
                </Box>
              )}
            </VStack>
          </VStack>
        )}

        {/* Users */}
        <HStack className="mb-2 items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">
            Users
            {selectedUsers.length > 0 ? ` (${selectedUsers.length})` : ""}
          </p>
        </HStack>

        <HStack className="mb-3 items-start gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-2">
          <Icon
            icon="mdi:information-outline"
            height={16}
            className="mt-0.5 shrink-0 text-blue-500"
          />
          <p className="text-xs text-blue-700">
            Don&apos;t see the user you&apos;re looking for? Add them to the
            Task Manager account first, then return here to grant access.
          </p>
        </HStack>

        {/* Search existing users */}
        {pickableUsers.length > 0 && (
          <Input
            type="text"
            placeholder="Search users"
            value={userSearch}
            onChange={(e: any) => setUserSearch(e.target.value)}
            className="mb-2"
          />
        )}

        {/* Users list — checkbox to grant/revoke access */}
        <VStack
          className={`max-h-56 divide-y divide-stone-100 overflow-y-auto rounded-md border ${
            usersError ? "border-red-400" : "border-stone-200"
          }`}
        >
          {filteredUsers.length > 0 ? (
            filteredUsers.map((u) => (
              <HStack
                key={u.emailId}
                className="items-center justify-between px-3 py-2 hover:bg-stone-50"
              >
                <CheckBoxWithLabel
                  id={`user-${u.emailId}`}
                  className="accent-orange-501 outline-hidden mr-2 cursor-pointer"
                  labelclassname="min-w-0 cursor-pointer text-sm text-stone-700"
                  checked={selectedEmails.has(u.emailId)}
                  onChange={() =>
                    selectedEmails.has(u.emailId)
                      ? removeUserEmail(u.emailId)
                      : addUserEmail(u.emailId)
                  }
                  label={
                    u.fullName && u.fullName !== u.emailId
                      ? `${u.fullName} (${u.emailId})`
                      : u.emailId
                  }
                />
              </HStack>
            ))
          ) : (
            <Box className="py-4">
              <p className="text-center text-xs text-stone-400">
                {pickableUsers.length === 0
                  ? "No users found — add one above"
                  : "No users match your search"}
              </p>
            </Box>
          )}
        </VStack>

        {usersError && (
          <span className="text-xxs text-red-500">
            Add at least one user
          </span>
        )}

        {/* Users with access — shown below the pickable list, removable */}
        {accessibleUsers.length > 0 && (
          <>
            <p className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-stone-400">
              Users with Access ({accessibleUsers.length})
            </p>
            <VStack className="mb-3 max-h-40 divide-y divide-stone-100 overflow-y-auto rounded-md border border-stone-200 bg-stone-50">
              {accessibleUsers.map((u) => (
                <HStack
                  key={u.emailId}
                  className="items-center justify-between gap-2 px-3 py-2"
                >
                  <HStack className="min-w-0 items-center gap-2">
                    <Icon
                      icon="mdi:check-circle"
                      height={14}
                      className="shrink-0 text-green-600"
                    />
                    <span className="truncate text-sm text-stone-700">
                      {u.fullName && u.fullName !== u.emailId
                        ? `${u.fullName} (${u.emailId})`
                        : u.emailId}
                    </span>
                  </HStack>
                  {deletingUserId === u.userId ? (
                    <Icon
                      icon="lucide:loader-2"
                      height={16}
                      className="shrink-0 animate-spin text-stone-400"
                    />
                  ) : accountUserEmailSet.has(u.emailId.toLowerCase()) ? (
                    <Icon
                      icon="mdi:trash-can-outline"
                      height={16}
                      className="shrink-0 cursor-pointer text-red-500 hover:text-red-700"
                      onClick={() => setRemoveTarget(u)}
                    />
                  ) : null}
                </HStack>
              ))}
            </VStack>
          </>
        )}

        {/* Access settings */}
        <p className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-stone-400">
          Access Mode
        </p>
        <HStack className="items-center gap-2">
          <input
            type="checkbox"
            id="access-mode-notify"
            name="accessMode"
            className="accent-orange-501 outline-hidden cursor-pointer"
            checked={!silentAccess}
            onChange={(e) => setSilentAccess(!e.target.checked)}
          />
          <label
            htmlFor="access-mode-notify"
            className="cursor-pointer text-sm text-stone-700"
          >
            Send notification emails
          </label>
          <Tooltip
            _className="w-48"
            text="This will send all selected users an email notification about their new access"
          >
            <Icon
              icon="material-symbols:info-outline"
              width={16}
              height={16}
              className="text-gray-400"
            />
          </Tooltip>
        </HStack>
      </div>

      {/* Sticky footer */}
      <HStack className="shrink-0 items-center justify-between gap-3 border-t border-stone-200 px-5 py-4">
        <p className="text-xs text-stone-500">
          {totalGrants > 0 ? (
            <>
              <span className="font-semibold text-stone-700">
                {totalGrants}
              </span>{" "}
              access {totalGrants === 1 ? "grant" : "grants"}
            </>
          ) : (
            "Select users to grant access"
          )}
        </p>
        <HStack className="gap-3">
          <SecondaryCTAButton event-name="Cancel Bulk Assign" onClick={onClose}>
            Cancel
          </SecondaryCTAButton>
          <PrimaryCTAButton
            event-name="Bulk Assign Users"
            loading={isLoading}
            onClick={handleSubmit}
          >
            Grant access
          </PrimaryCTAButton>
        </HStack>
      </HStack>

      {/* Confirm before revoking a user's access to this company */}
      <Dialog
        open={Boolean(removeTarget)}
        onClose={() => setRemoveTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        {removeTarget && (
          <VStack className="gap-4 p-6">
            <HStack className="items-start gap-3">
              <HStack className="h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-50">
                <Icon icon="mdi:trash-can-outline" height={28} className="text-red-500" />
              </HStack>
              <VStack className="gap-1.5">
                <p className="text-base font-semibold text-stone-800 my-2">
                  Remove User Access
                </p>
                <p className="text-sm text-stone-500">
                  Are you sure you want to remove access for{" "}
                  <span className="font-semibold text-stone-700">
                    {removeTarget.emailId}
                  </span>
                  ? This user will no longer be able to access this
                  company&apos;s data.
                </p>
              </VStack>
            </HStack>
            <HStack className="justify-end gap-3 pt-1">
              <button
                type="button"
                onClick={() => handleRemoveAccess(removeTarget)}
                className="rounded-sm bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
              >
                Remove Access
              </button>
              <button
                type="button"
                onClick={() => setRemoveTarget(null)}
                className="rounded-sm border border-red-500 px-4 py-2 text-sm font-semibold text-red-500 hover:bg-red-50"
              >
                Cancel
              </button>
            </HStack>
          </VStack>
        )}
      </Dialog>
    </div>
  );
}

export default function BulkAssignUsersDrawer(
  props: Props & { open: boolean }
) {
  const { open, ...rest } = props;
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={rest.onClose}
      PaperProps={{ className: "w-[min(94vw,480px)]" }}
    >
      {open && <BulkAssignDrawerContent {...rest} />}
    </Drawer>
  );
}
